import React, { useEffect } from "react"
import styled from "styled-components"
import { useParams } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import { useDispatch, useSelector } from "react-redux"
import { fetchSubmissionById, selectSubmissionsById } from "./slice"
import NavMain from "../../app/NavMain"
import DataPageHeader from "../../ui/DataPageHeader"
import Layout from "../../ui/Layout"
import ScrollablePage from "../../ui/ScrollablePage"
import Paper from "../../ui/Paper"
import ErrorMsg from "../../ui/ErrorMsg"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import { Attribute } from "../../ui/Attribute"
import PropTypes from "prop-types"
import Timestamp from "../../ui/Timestamp"
import { selectAdvancedMode } from "../settings/slice"
import {
  fetchSamples,
  selectSamplesByContext,
  selectSamplesByIds,
} from "../samples/slice"
import SamplesTable from "../samples/SamplesTable"

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1rem;
`

/**
 * Renders a data submission in full
 * @returns {JSX.Element}
 * @constructor
 */
const Submission = () => {
  const dispatch = useDispatch()
  const submissionsByID = useSelector(selectSubmissionsById)
  const { id } = useParams()
  const submission = submissionsByID[id]

  const status = submission?._loadStatus

  useEffect(() => {
    dispatch(fetchSubmissionById(id))
  }, [])

  return (
    <Layout>
      <NavMain />
      <Helmet>
        <title>Data submission {id} - Astrosite</title>
      </Helmet>
      {status === "loading" && (
        <Backdrop>
          <LoadingSpinner />
        </Backdrop>
      )}
      <ScrollablePage>
        <DataPageHeader
          title={`Data submission - ${id}`}
          subtitle={
            <>
              {status === "rejected" && (
                <ErrorMsg
                  msg={`There is a problem fetching the data submission with ID ${id}`}
                  showIcon={true}
                />
              )}
            </>
          }
        />
        <Container>
          <Paper>
            <SubmissionDetails submission={submission} />
          </Paper>
          <Paper>
            <h3>Config</h3>
            {submission?.config ? (
              <pre>{JSON.stringify(submission?.config, null, 2)}</pre>
            ) : (
              <span>None specified</span>
            )}
          </Paper>
        </Container>
        {submission?.id && (
          <SubmissionSamples
            sampleIds={submission?.samples}
            submissionId={submission?.id}
          />
        )}
      </ScrollablePage>
    </Layout>
  )
}

const SubmissionDetails = ({ submission = {} }) => {
  const advancedMode = useSelector(selectAdvancedMode)

  return (
    <div>
      <h3>Details</h3>
      <Attribute>
        <dt>Label</dt>
        <dd>{submission.label || "-"}</dd>
      </Attribute>
      <Attribute>
        <dt>Destination</dt>
        <dd>{submission.destination || "-"}</dd>
      </Attribute>
      <Attribute>
        <dt>Submitted at at</dt>
        <dd>
          <Timestamp utc={advancedMode}>{submission?.created_at}</Timestamp>
        </dd>
      </Attribute>
    </div>
  )
}

SubmissionDetails.propTypes = {
  submission: PropTypes.object,
}

// Renders a table of the samples in this data submission
const SubmissionSamples = ({ submissionId, sampleIds = [] }) => {
  const dispatch = useDispatch()

  useEffect(() => {
    if (submissionId && sampleIds.length > 0) {
      dispatch(
        fetchSamples({
          filter: {
            sample_ids: sampleIds,
          },
          page: {
            offset: 0,
            limit: 1000,
          },
          context: `submission/${submissionId}`,
        })
      )
    }
  }, [submissionId])

  // we get any matching sample for this submission
  const sampleMatches = useSelector((state) =>
    selectSamplesByContext(state, `submission/${submissionId}`)
  )
  // We get the full objects from the ids the match
  const samples = useSelector((state) =>
    selectSamplesByIds(state, sampleMatches?.ids || [])
  )

  return (
    submissionId && (
      <div>
        {sampleMatches?._loadStatus === "loading" && (
          <div>...loading samples</div>
        )}
        {sampleMatches?._loadStatus === "rejected" && (
          <ErrorMsg
            msg={
              "There is a problem fetching the samples for this data submission"
            }
            showIcon={true}
          />
        )}
        {sampleMatches?._loadStatus !== "loading" &&
          sampleMatches?.page?.total_count && (
            <div>
              showing {samples.length} of {sampleMatches?.page?.total_count}{" "}
              samples
            </div>
          )}
        <SamplesTable samples={samples} stickyHeader={false} />
      </div>
    )
  )
}

SubmissionSamples.propTypes = {
  submissionId: PropTypes.string.isRequired,
  sampleIds: PropTypes.array,
}

export default Submission
